import { app } from '../common/api/apiClient'
import {
    UpdateAddressesPayload,
    ContactDetails,
    type User as UserT,
    CreateUserPayload,
    Name,
    UsagePolicyKey,
    UsagePolicy,
    AccountType,
    RewardsT,
    FeaturePromotion,
    FeedbackId,
} from '@/common/types'
import type { UserTfnRequest } from '@six-invest/api-types'
import { API_ROUTES } from '@/common/constants'
import { SurveyT } from '@/user/survey/SurveyPage'
import { insertStringVariables } from '@/common/utils/utils'

export async function triggerSetupUser() {
    const res = await app.put(API_ROUTES.userSetup)
    return res.data
}

export async function fetchUser() {
    const res = await app.get(API_ROUTES.user)
    return res.data as UserT
}

export async function fetchUserRewards() {
    const res = await app.get(API_ROUTES.userRewards)
    return res.data as RewardsT
}

export async function createUser(data: CreateUserPayload): Promise<UserT> {
    const res = await app.post(API_ROUTES.user, data)
    return res.data as UserT
}

export const updateName = async (data: Name) => {
    const response = await app.put(API_ROUTES.userName, data)
    return response.data
}

export const updateMobile = async (data?: string) => {
    const response = await app.put(API_ROUTES.userMobile, { mobile: data })
    return response.data
}

export const sendMobileOtp = async () => {
    const response = await app.post(API_ROUTES.sendMobileOtp)
    return response.data
}

export const verifyMobileOtp = async (data: string) => {
    const response = await app.post(API_ROUTES.verifyMobileOtp, { code: data })
    return response.data
}

export const updateSurveyData = async (data: SurveyT) => {
    const response = await app.put(API_ROUTES.survey, data)
    return response.data
}

export const updateAccountType = async (accountType: AccountType) => {
    const response = await app.put(API_ROUTES.userAccountType, { accountType })
    return response.data
}

export const updateContactDetails = async (data: ContactDetails) => {
    const response = await app.put(API_ROUTES.userContactDetails, data)
    return response.data
}

export const updateAddresses = async (data: UpdateAddressesPayload) => {
    const response = await app.put(API_ROUTES.userAddresses, data)
    return response.data
}

export const updateTfn = async (data: UserTfnRequest) => {
    const response = await app.put(API_ROUTES.userTfn, data)
    return response.data
}

export const updateTodos = async (todoId: string) => {
    const response = await app.post(insertStringVariables(API_ROUTES.userTodos, todoId))
    return response.data
}

export const viewFeaturePromotion = async (featurePromotion: FeaturePromotion) => {
    const response = await app.post(insertStringVariables(API_ROUTES.userFeaturePromotionsViewed, featurePromotion))
    return response.data
}

export const submitFeedback = async (
    feedbackId: FeedbackId,
    data?: {
        questions: { question: string; response: string | string[] }[]
        referenceId: string
    },
) => {
    const response = await app.post(insertStringVariables(API_ROUTES.userFeedback, feedbackId), data)
    return response.data
}

export const searchAddress = async (query: string) => {
    const res = await app.post(API_ROUTES.addressSearch, {
        query,
    })
    return res.data
}

export const searchAddressById = async (addressId: string) => {
    const res = await app.get(`${API_ROUTES.addressSearchById}/${addressId}`)
    return res.data
}

export const getUsagePolicy = async (policyKey: UsagePolicyKey) => {
    const res = await app.get(insertStringVariables(API_ROUTES.usagePolicies, policyKey))
    return res.data as UsagePolicy
}

export const acceptPolicy = async (versionId: string) => {
    const res = await app.post(API_ROUTES.acceptPolicy, { versionId })
    return res.data
}

export const acceptTerms = async (versionIds: string[]) => {
    const res = await app.post(API_ROUTES.acceptTerms, { versionIds })
    return res.data
}

export const contactSupport = async (data: { message: string }) => {
    const res = await app.post(API_ROUTES.contactSupport, { message: data.message })
    return res.data
}

export const trackResetPassword = async (data: { emailId: string }) => {
    const res = await app.post(API_ROUTES.trackPasswordReset, { emailId: data.emailId })
    return res.data
}
